// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as StartIcon,
  Straighten as MeasurementIcon,
  CloudUpload as UploadIcon,
  Calculate as EstimateIcon,
  Shield as ClaimIcon,
  CheckCircle as CheckIcon,
  AccessTime as ClockIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Assignment as ChecklistIcon,
} from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import { GamifyToast } from '../../common/CustomToasts';
import {
  uploadWorkflowReport,
  replaceWorkflowReport,
  removeWorkflowReport,
  resolveDealDocumentUrl,
  SOURCE_USER_UPLOAD,
  INACTIVE_STATUSES,
  isExternalReadOnlyUser,
} from '../../services/workflowReportUpload';
import featureFlag from '../../common/featureFlag';
import DealReports from '../../routes/deal/components/DealReports';
import DealEstimates from '../../routes/deal/components/DealEstimates';
import DealClaims from '../../routes/deal/components/DealClaims';
import DealInspectionQA from '../../routes/deal/components/DealInspectionQA';
import NotesToHankChat from '../NotesToHankChat/NotesToHankChat';

// ── GraphQL ─────────────────────────────────────────────────

const maintainMeasurementOrderMutation = /* GraphQL */ `
  mutation MaintainMeasurementOrder($request: AWSJSON!) {
    maintainMeasurementOrder(request: $request)
  }
`;

const getIRAiReportQuery = /* GraphQL */ `
  query GetIRAiReport(
    $latitude: Float!
    $longitude: Float!
    $requestId: String
  ) {
    getIRAiReport(
      latitude: $latitude
      longitude: $longitude
      requestId: $requestId
    ) {
      status
      message
      requestId
    }
  }
`;

const requestIRHumanReportMutation = /* GraphQL */ `
  mutation RequestIRHumanReport(
    $latitude: Float!
    $longitude: Float!
    $requestId: String
  ) {
    requestIRHumanReport(
      latitude: $latitude
      longitude: $longitude
      requestId: $requestId
    ) {
      humanReportId
      requestId
    }
  }
`;

const getTaskListItemsQuery = /* GraphQL */ `
  query GetTaskListItems(
    $dealID: String!
    $userID: String
    $autoCreate: Boolean
  ) {
    getTaskListItems(dealID: $dealID, userID: $userID, autoCreate: $autoCreate)
  }
`;

// ── Types ───────────────────────────────────────────────────

interface MeasurementOrder {
  id: string;
  reportProvider: string;
  status: string;
  pdfUrl?: string;
  reportUrl?: string;
  orderedAt: string;
}

interface TaskList {
  id: string;
  name: string;
  updatedAt: string | null;
  inspectionType: string | null;
}

export interface ClaimTabProps {
  dealID: string;
  dealInfo?: any;
  pollTick?: number;
}

// ── Helpers ─────────────────────────────────────────────────

function parseGraphQLResponse(raw: any): any {
  let parsed = raw;
  while (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      break;
    }
  }
  return parsed;
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusColor = (status: string): string => {
  const s = status?.toUpperCase();
  switch (s) {
    case 'READY':
    case 'COMPLETED':
    case 'COMPLETE':
      return '#4CAF50';
    case 'PROCESSING':
    case 'ORDERED':
      return '#FF9800';
    case 'FAILED':
      return '#F44336';
    default:
      return '#9E9E9E';
  }
};

const getStatusLabel = (status: string): string => {
  const s = status?.toUpperCase();
  switch (s) {
    case 'READY':
    case 'COMPLETED':
    case 'COMPLETE':
      return 'COMPLETE';
    case 'PROCESSING':
      return 'PROCESSING';
    case 'ORDERED':
      return 'ORDERED';
    case 'FAILED':
      return 'FAILED';
    case 'PENDING':
      return 'PENDING';
    default:
      return status?.toUpperCase() || 'UNKNOWN';
  }
};

const getStatusIcon = (status: string) => {
  const s = status?.toUpperCase();
  switch (s) {
    case 'READY':
    case 'COMPLETED':
    case 'COMPLETE':
      return <CheckIcon sx={{ fontSize: 14 }} />;
    case 'FAILED':
      return <ErrorIcon sx={{ fontSize: 14 }} />;
    default:
      return <ClockIcon sx={{ fontSize: 14 }} />;
  }
};

const isViewable = (status: string): boolean => {
  const s = status?.toUpperCase();
  return s === 'COMPLETED' || s === 'READY' || s === 'COMPLETE';
};

// ── Collapsible Section ─────────────────────────────────────

const CollapsibleSection: React.FC<{
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, expanded, onToggle, children }) => {
  const { theme } = useTheme();

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          cursor: 'pointer',
          borderBottom: `1px solid ${theme.stroke}`,
          '&:hover': { backgroundColor: theme.background2 },
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: theme.text }}>
          {title}
        </Typography>
        {expanded ? (
          <ExpandLessIcon sx={{ color: theme.textLight }} />
        ) : (
          <ExpandMoreIcon sx={{ color: theme.textLight }} />
        )}
      </Box>
      <Collapse in={expanded}>
        <Box>{children}</Box>
      </Collapse>
    </Box>
  );
};

// ── Claim Row (matches mobile ClaimRow visual style) ────────

const ClaimRow: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
  statusChip?: { label: string; status: string } | null;
  showOverflow?: boolean;
  disabled?: boolean;
  menuOptions?: { label: string; action: string; destructive?: boolean }[];
  onMenuAction?: (action: string) => void;
}> = ({
  icon,
  title,
  description,
  onClick,
  statusChip,
  showOverflow = true,
  disabled = false,
  menuOptions,
  onMenuAction,
}) => {
  const { theme } = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  return (
    <Box
      onClick={!disabled ? onClick : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.stroke || '#E0E0E0'}`,
        cursor: !disabled && onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.4 : 1,
        '&:hover':
          !disabled && onClick ? { backgroundColor: theme.background2 } : {},
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1.5,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: theme.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: theme.textLight,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {description}
        </Typography>
      </Box>
      {statusChip && (
        <Chip
          icon={getStatusIcon(statusChip.status)}
          label={statusChip.label}
          size="small"
          sx={{
            ml: 1,
            backgroundColor: `${getStatusColor(statusChip.status)}20`,
            color: getStatusColor(statusChip.status),
            fontWeight: 600,
            fontSize: 11,
            '& .MuiChip-icon': {
              color: getStatusColor(statusChip.status),
            },
          }}
        />
      )}
      {menuOptions && menuOptions.length > 0 ? (
        <>
          <IconButton
            size="small"
            sx={{ ml: 1, color: theme.textLight }}
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
          >
            <Box sx={{ fontSize: 20, lineHeight: 1 }}>...</Box>
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            onClick={(e) => e.stopPropagation()}
          >
            {menuOptions.map((opt) => (
              <MenuItem
                key={opt.action}
                onClick={() => {
                  setMenuAnchor(null);
                  onMenuAction?.(opt.action);
                }}
                sx={opt.destructive ? { color: '#D32F2F' } : {}}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        showOverflow && (
          <Box sx={{ ml: 1, color: theme.textLight, fontSize: 20 }}>...</Box>
        )
      )}
    </Box>
  );
};

// ── Main Component ──────────────────────────────────────────

const ClaimTab: React.FC<ClaimTabProps> = ({ dealID, dealInfo, pollTick }) => {
  const { theme } = useTheme();

  // Section expanded states
  const [inspectionExpanded, setInspectionExpanded] = useState(true);
  const [reportsExpanded, setReportsExpanded] = useState(true);
  const [uploadsExpanded, setUploadsExpanded] = useState(true);

  // Expandable sub-panels (show full component when row is clicked)
  const [estimatesOpen, setEstimatesOpen] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(false);

  // Inspection checklists
  const [taskLists, setTaskLists] = useState<TaskList[]>([]);

  // Measurement orders
  const [measurementOrders, setMeasurementOrders] = useState<
    MeasurementOrder[]
  >([]);
  const [showMeasurementPopup, setShowMeasurementPopup] = useState(false);
  // External contractor users (paiv_admin_ro, e.g. the Xactimate team) can
  // upload reports but must never see purchase options — orders charge the
  // customer org (GG-3433).
  const [isExternalUser, setIsExternalUser] = useState(false);
  useEffect(() => {
    isExternalReadOnlyUser().then(setIsExternalUser);
  }, []);
  const [isOrderingMeasurement, setIsOrderingMeasurement] = useState(false);

  // Modals
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [activeChecklistName, setActiveChecklistName] = useState<string | null>(
    null
  );
  const [showAIPromptChat, setShowAIPromptChat] = useState(false);
  const [showInspectionModes, setShowInspectionModes] = useState(false);

  // Data presence flags for highlight state machine (mirrors mobile)
  const [hasEstimate] = useState(false);
  const [hasClaimSubmitted] = useState(false);
  // ── Data fetching ───────────────────────────────────────────

  const refreshChecklist = useCallback(async () => {
    if (!dealID) return;
    try {
      const userID = (global as any)?.me?.id;
      const result: any = await API.graphql(
        graphqlOperation(getTaskListItemsQuery, {
          dealID,
          userID,
          autoCreate: false,
        })
      );
      const json = JSON.parse(result.data.getTaskListItems);
      if (json.success && json.data?.taskLists?.length > 0) {
        setTaskLists(
          json.data.taskLists.map((tl: any) => ({
            id: tl.id,
            name: tl.taskListName || tl.templateName || 'Inspection Checklist',
            updatedAt: tl.updatedAt || tl.createdAt || null,
            inspectionType: tl.inspectionType || null,
          }))
        );
      } else {
        setTaskLists([]);
      }
    } catch (err) {
      console.error('[ClaimTab] Failed to fetch checklists', err);
    }
  }, [dealID]);

  const fetchMeasurementOrders = useCallback(async () => {
    if (!dealID) return;
    try {
      const request = JSON.stringify({
        operation: 'getMeasurementOrdersByDeal',
        dealID,
      });
      const result: any = await API.graphql(
        graphqlOperation(maintainMeasurementOrderMutation, { request })
      );
      const parsed = parseGraphQLResponse(
        result?.data?.maintainMeasurementOrder
      );
      if (parsed?.success && parsed.data?.measurementOrders?.length > 0) {
        setMeasurementOrders(parsed.data.measurementOrders);
      } else {
        setMeasurementOrders([]);
      }
    } catch (err) {
      console.error('[ClaimTab] Failed to fetch measurement orders:', err);
    }
  }, [dealID]);

  useEffect(() => {
    refreshChecklist();
    fetchMeasurementOrders();
  }, [refreshChecklist, fetchMeasurementOrders]);

  const pollTickOnMountRef = React.useRef(pollTick);
  useEffect(() => {
    if (pollTick !== pollTickOnMountRef.current && pollTick && pollTick > 0) {
      refreshChecklist();
      fetchMeasurementOrders();
    }
  }, [pollTick, refreshChecklist, fetchMeasurementOrders]);

  // ── Highlight state machine (mirrors mobile) ───────────────

  const hasChecklist = taskLists.length > 0;
  const hasMeasurements = measurementOrders.length > 0;

  type HighlightedAction =
    | 'startInspection'
    | 'orderMeasurements'
    | 'orderEstimate'
    | 'submitClaim';

  const highlightedAction = useMemo<HighlightedAction>(() => {
    if (!hasChecklist) return 'startInspection';
    if (!hasMeasurements) return 'orderMeasurements';
    if (!hasEstimate) return 'orderEstimate';
    return 'submitClaim';
  }, [hasChecklist, hasMeasurements, hasEstimate]);

  // ── Handlers ──────────────────────────────────────────────

  const createMeasurementOrderRecord = useCallback(
    async (type: string) => {
      if (!dealID) return;
      try {
        const request = JSON.stringify({
          operation: 'createMeasurementOrder',
          dealID,
          reportProvider: type,
          dealName: dealInfo?.name || dealInfo?.customerName || '',
          dealAddress:
            dealInfo?.address ||
            dealInfo?.streetAddress ||
            dealInfo?.fullAddress ||
            '',
        });
        await API.graphql(
          graphqlOperation(maintainMeasurementOrderMutation, { request })
        );
      } catch (err) {
        console.error(
          '[ClaimTab] Failed to create measurement order record:',
          err
        );
      }
    },
    [dealID, dealInfo]
  );

  const handleOrderIRAiReport = useCallback(async () => {
    const lat = dealInfo?.pinAddress?.location?.lat;
    const lng = dealInfo?.pinAddress?.location?.lon;
    if (!lat || !lng) {
      GamifyToast.error('No coordinates available for this property.');
      return;
    }
    setIsOrderingMeasurement(true);
    setShowMeasurementPopup(false);
    try {
      const result: any = await API.graphql(
        graphqlOperation(getIRAiReportQuery, {
          latitude: lat,
          longitude: lng,
          requestId: dealID,
        })
      );
      const irResponse = result?.data?.getIRAiReport;
      if (irResponse?.status === 'error' || irResponse?.status === 'failed') {
        GamifyToast.error('Instant Roofer AI report failed. Please try again.');
      } else {
        GamifyToast.success('Instant Roofer AI report ordered.');
        createMeasurementOrderRecord('Instant Roofer AI');
        fetchMeasurementOrders();
      }
    } catch (err) {
      console.error('[ClaimTab] IR AI Report failed:', err);
      GamifyToast.error('Failed to order report. Please try again.');
    } finally {
      setIsOrderingMeasurement(false);
    }
  }, [dealInfo, dealID, createMeasurementOrderRecord, fetchMeasurementOrders]);

  const handleOrderIRHumanReport = useCallback(async () => {
    const lat = dealInfo?.pinAddress?.location?.lat;
    const lng = dealInfo?.pinAddress?.location?.lon;
    if (!lat || !lng) {
      GamifyToast.error('No coordinates available for this property.');
      return;
    }
    setIsOrderingMeasurement(true);
    setShowMeasurementPopup(false);
    try {
      await API.graphql(
        graphqlOperation(requestIRHumanReportMutation, {
          latitude: lat,
          longitude: lng,
          requestId: dealID,
        })
      );
      GamifyToast.success('Instant Roofer Human report ordered.');
      createMeasurementOrderRecord('Instant Roofer Human');
      fetchMeasurementOrders();
    } catch (err) {
      console.error('[ClaimTab] IR Human Report failed:', err);
      GamifyToast.error('Failed to order report. Please try again.');
    } finally {
      setIsOrderingMeasurement(false);
    }
  }, [dealInfo, dealID, createMeasurementOrderRecord, fetchMeasurementOrders]);

  const handleViewMeasurementReport = async (order: MeasurementOrder) => {
    const s3Key = order.pdfUrl || order.reportUrl;
    if (isViewable(order.status) && ((order as any).dealFileId || s3Key)) {
      try {
        GamifyToast.info('Loading report...');
        // DealFile record first (holds the CURRENT key; uploads can be
        // replaced/re-keyed), legacy pdfUrl key fallback (GG-3433).
        const signedUrl = await resolveDealDocumentUrl({
          dealFileId: (order as any).dealFileId,
          s3Key,
        });
        if (!signedUrl) throw new Error('No resolvable report file');
        // Fetch as blob to avoid Content-Disposition: attachment forcing download
        const response = await fetch(signedUrl as string);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(
          new Blob([blob], { type: 'application/pdf' })
        );
        window.open(blobUrl, '_blank');
      } catch (err) {
        console.error('[ClaimTab] Failed to load report:', err);
        GamifyToast.error('Failed to load report. Please try again.');
      }
    } else if (
      order.status?.toUpperCase() === 'PROCESSING' ||
      order.status?.toUpperCase() === 'ORDERED'
    ) {
      GamifyToast.info(
        'Your measurement report is being prepared. Please check back shortly.'
      );
    } else {
      GamifyToast.error('Report URL not available for this order.');
    }
  };

  // ── Upload / replace / remove a user's own measurement report (GG-3433,
  // mirrors mobile): shared pipeline -> S3 -> order(source=USER_UPLOAD) ->
  // DealFile -> READY + dealFileId.
  const dealCtx = () => ({
    dealId: dealID,
    dealName: dealInfo?.name || dealInfo?.customerName || '',
    dealAddress:
      dealInfo?.address ||
      dealInfo?.streetAddress ||
      dealInfo?.fullAddress ||
      '',
  });

  const handleUploadMeasurement = async () => {
    setShowMeasurementPopup(false);
    const res = await uploadWorkflowReport('measurement', dealCtx());
    if (res.success) {
      GamifyToast.success('Measurement report uploaded.');
      fetchMeasurementOrders();
    } else if (!res.canceled) {
      GamifyToast.error(res.error || 'Upload failed. Please try again.');
    }
  };

  const handleMeasurementMenuAction = async (action: string) => {
    if (!latestMeasurement) return;
    if (action === 'view') {
      handleViewMeasurementReport(latestMeasurement);
      return;
    }
    if (action === 'replace') {
      const res = await replaceWorkflowReport('measurement', dealCtx());
      if (res.success) {
        GamifyToast.success('Measurement report replaced.');
        fetchMeasurementOrders();
      } else if (!res.canceled) {
        GamifyToast.error(res.error || 'Replace failed. Please try again.');
      }
      return;
    }
    if (action === 'remove') {
      const res = await removeWorkflowReport('measurement', dealID);
      if (res.success) {
        GamifyToast.success('Measurement report removed.');
        fetchMeasurementOrders();
      } else {
        GamifyToast.error(res.error || 'Remove failed. Please try again.');
      }
    }
  };

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png';
    input.onchange = () => {
      if (input.files && input.files.length > 0) {
        GamifyToast.info(
          'File upload functionality will be available in a future update.'
        );
      }
    };
    input.click();
  };

  // Derive measurement report display for the row: the newest ACTIVE order.
  // CANCELLED/FAILED rows mean "no measurement" (mirrors mobile + the chat
  // agent's gating) so a removed upload re-enables ordering.
  const latestMeasurement =
    measurementOrders.find(
      (o: any) => o && !INACTIVE_STATUSES.includes(o.status)
    ) || null;

  // ── Render ────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Inspection Section ─────────────────────────────── */}
      <CollapsibleSection
        title="Inspection"
        expanded={inspectionExpanded}
        onToggle={() => setInspectionExpanded((prev) => !prev)}
      >
        {/* Existing inspection checklists */}
        {taskLists.map((tl) => (
          <ClaimRow
            key={tl.id}
            icon={
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  backgroundColor: '#35829E20',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChecklistIcon sx={{ fontSize: 18, color: '#35829E' }} />
              </Box>
            }
            title={tl.name}
            description={`Continue Inspection${
              tl.updatedAt ? `\nLast updated: ${formatDate(tl.updatedAt)}` : ''
            }`}
            onClick={() => {
              setActiveChecklistName(tl.name);
              setShowChecklistModal(true);
            }}
          />
        ))}

        {/* Start New Inspection - opens AI chat on web */}
        <ClaimRow
          icon={
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                backgroundColor:
                  highlightedAction === 'startInspection'
                    ? '#F47B2620'
                    : '#E0E0E0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StartIcon
                sx={{
                  fontSize: 18,
                  color:
                    highlightedAction === 'startInspection'
                      ? '#F47B26'
                      : '#AAAAAA',
                }}
              />
            </Box>
          }
          title="Start New Inspection"
          description="Begin Inspection of a New Building"
          showOverflow={false}
          onClick={() => setShowInspectionModes(true)}
        />
      </CollapsibleSection>

      {/* ── Reports Section ────────────────────────────────── */}
      <CollapsibleSection
        title="Reports"
        expanded={reportsExpanded}
        onToggle={() => setReportsExpanded((prev) => !prev)}
      >
        {/* Damage Reports - rendered inline but without its own section header feel */}
        <DealReports dealID={dealID} dealInfo={dealInfo} />

        {/* Measurement Report row (matches mobile) */}
        <ClaimRow
          icon={
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                backgroundColor: '#35829E20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MeasurementIcon sx={{ fontSize: 18, color: '#35829E' }} />
            </Box>
          }
          title={
            latestMeasurement
              ? `Measurement Report — ${latestMeasurement.reportProvider}`
              : 'Measurement Report'
          }
          description={
            latestMeasurement
              ? getStatusLabel(latestMeasurement.status)
              : 'Order Measurement Report'
          }
          statusChip={
            latestMeasurement
              ? {
                  label: getStatusLabel(latestMeasurement.status),
                  status: latestMeasurement.status,
                }
              : null
          }
          disabled={highlightedAction === 'startInspection'}
          onClick={() => {
            if (latestMeasurement && isViewable(latestMeasurement.status)) {
              handleViewMeasurementReport(latestMeasurement);
            } else {
              setShowMeasurementPopup(true);
            }
          }}
          menuOptions={
            latestMeasurement
              ? [
                  ...(isViewable(latestMeasurement.status)
                    ? [{ label: 'View', action: 'view' }]
                    : []),
                  // Replace/Remove only for user uploads — purchased reports
                  // are view-only (mirrors mobile GG-3432).
                  ...((latestMeasurement as any).source === SOURCE_USER_UPLOAD
                    ? [
                        { label: 'Replace report…', action: 'replace' },
                        {
                          label: 'Remove report',
                          action: 'remove',
                          destructive: true,
                        },
                      ]
                    : []),
                ]
              : []
          }
          onMenuAction={handleMeasurementMenuAction}
        />

        {/* Order Estimate row (matches mobile) - click expands full DealEstimates */}
        <ClaimRow
          icon={
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                backgroundColor: '#C9E7EE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EstimateIcon sx={{ fontSize: 18, color: '#35829E' }} />
            </Box>
          }
          title="Order Estimate"
          description="Order or upload an Xactimate estimate"
          disabled={
            highlightedAction === 'startInspection' ||
            highlightedAction === 'orderMeasurements'
          }
          onClick={() => setEstimatesOpen((prev) => !prev)}
        />
        <Collapse in={estimatesOpen}>
          <Box sx={{ borderBottom: `1px solid ${theme.stroke || '#E0E0E0'}` }}>
            <DealEstimates dealID={dealID} dealInfo={dealInfo} />
          </Box>
        </Collapse>

        {/* Send to Claims Management row (matches mobile) - click expands full DealClaims */}
        <ClaimRow
          icon={
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                backgroundColor: '#C9E7EE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ClaimIcon sx={{ fontSize: 18, color: '#35829E' }} />
            </Box>
          }
          title="Send to Claims Management"
          description="Send to claims management"
          disabled={highlightedAction !== 'submitClaim' || hasClaimSubmitted}
          onClick={() => setClaimsOpen((prev) => !prev)}
        />
        <Collapse in={claimsOpen}>
          <Box sx={{ borderBottom: `1px solid ${theme.stroke || '#E0E0E0'}` }}>
            <DealClaims
              dealID={dealID}
              dealInfo={dealInfo}
              pollTick={pollTick}
            />
          </Box>
        </Collapse>
      </CollapsibleSection>

      {/* ── Uploads Section ────────────────────────────────── */}
      <CollapsibleSection
        title="Uploads"
        expanded={uploadsExpanded}
        onToggle={() => setUploadsExpanded((prev) => !prev)}
      >
        <ClaimRow
          icon={
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                backgroundColor: '#35829E20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UploadIcon sx={{ fontSize: 18, color: '#35829E' }} />
            </Box>
          }
          title="Upload"
          description="Upload documents and files"
          onClick={handleFileUpload}
        />
      </CollapsibleSection>

      {/* ── Measurement Report Order Popup ──────────────── */}
      <Dialog
        open={showMeasurementPopup}
        onClose={() => setShowMeasurementPopup(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: theme.background1, borderRadius: 3 },
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: theme.text,
              textAlign: 'center',
              mb: 2,
            }}
          >
            Order Measurement Report
          </Typography>

          {!isExternalUser && featureFlag('EVMeasurements') && (
            <Box
              sx={{
                border: `1px solid ${theme.stroke}`,
                borderRadius: 2,
                p: 2,
                mb: 1.5,
                opacity: 0.45,
                cursor: 'default',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, color: theme.text, fontSize: 14 }}
                >
                  EagleView Premium Roof Report
                </Typography>
                <Chip
                  label="Mobile"
                  size="small"
                  sx={{
                    fontSize: 10,
                    height: 20,
                    backgroundColor: `${theme.textLight}20`,
                    color: theme.textLight,
                  }}
                />
              </Box>
              <Typography
                sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}
              >
                Order EagleView reports from the mobile app.
              </Typography>
            </Box>
          )}

          {!isExternalUser && featureFlag('IRMeasurements') && (
            <>
              <Box
                onClick={
                  !isOrderingMeasurement ? handleOrderIRAiReport : undefined
                }
                sx={{
                  border: `1px solid ${theme.stroke}`,
                  borderRadius: 2,
                  p: 2,
                  mb: 1.5,
                  cursor: isOrderingMeasurement ? 'default' : 'pointer',
                  '&:hover': !isOrderingMeasurement
                    ? {
                        borderColor: theme.primary,
                        backgroundColor: `${theme.primary}08`,
                      }
                    : {},
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, color: theme.text, fontSize: 14 }}
                >
                  Instant Roofer AI Report
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}
                >
                  $3.00
                </Typography>
              </Box>

              <Box
                onClick={
                  !isOrderingMeasurement ? handleOrderIRHumanReport : undefined
                }
                sx={{
                  border: `1px solid ${theme.stroke}`,
                  borderRadius: 2,
                  p: 2,
                  mb: 1.5,
                  cursor: isOrderingMeasurement ? 'default' : 'pointer',
                  '&:hover': !isOrderingMeasurement
                    ? {
                        borderColor: theme.primary,
                        backgroundColor: `${theme.primary}08`,
                      }
                    : {},
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, color: theme.text, fontSize: 14 }}
                >
                  Instant Roofer Human Report
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}
                >
                  $10.00
                </Typography>
              </Box>
            </>
          )}

          {isOrderingMeasurement && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 1,
              }}
            >
              <CircularProgress
                size={20}
                sx={{ color: theme.primary, mr: 1 }}
              />
              <Typography sx={{ fontSize: 13, color: theme.textLight }}>
                Ordering report...
              </Typography>
            </Box>
          )}

          {/* Upload your own report — always available, no flag (GG-3433,
              mirrors mobile's popup option) */}
          <Box
            onClick={
              !isOrderingMeasurement ? handleUploadMeasurement : undefined
            }
            sx={{
              border: `1px solid ${theme.stroke}`,
              borderRadius: 2,
              p: 2,
              mb: 1.5,
              cursor: isOrderingMeasurement ? 'default' : 'pointer',
              '&:hover': !isOrderingMeasurement
                ? {
                    borderColor: theme.primary,
                    backgroundColor: `${theme.primary}08`,
                  }
                : {},
            }}
          >
            <Typography
              sx={{ fontWeight: 600, color: theme.text, fontSize: 14 }}
            >
              Upload Existing Report
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}>
              Free — use a PDF or XML report you already have
            </Typography>
          </Box>

          <Box
            onClick={() => setShowMeasurementPopup(false)}
            sx={{
              border: `1px solid ${theme.stroke}`,
              borderRadius: 2,
              py: 1.5,
              mb: 2,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: theme.background2 },
            }}
          >
            <Typography sx={{ fontSize: 13, color: theme.textLight }}>
              Never Mind
            </Typography>
          </Box>
        </Box>
      </Dialog>

      {/* ── Inspection Mode Picker ─────────────────────────── */}
      <Dialog
        open={showInspectionModes}
        onClose={() => setShowInspectionModes(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: theme.background1, borderRadius: 3 },
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: theme.text,
              textAlign: 'center',
              mb: 2,
            }}
          >
            Start New Inspection
          </Typography>

          {/* Guided Inspection - mobile only */}
          <Box
            sx={{
              border: `1px solid ${theme.stroke}`,
              borderRadius: 2,
              p: 2,
              mb: 1.5,
              opacity: 0.45,
              cursor: 'default',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{ fontWeight: 600, color: theme.text, fontSize: 14 }}
              >
                Guided Inspection
              </Typography>
              <Chip
                label="Mobile Only"
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  backgroundColor: `${theme.textLight}20`,
                  color: theme.textLight,
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}>
              Step-by-step walkthrough. Just follow along and we will build your
              report.
            </Typography>
          </Box>

          {/* Freestyle Inspection - mobile only */}
          <Box
            sx={{
              border: `1px solid ${theme.stroke}`,
              borderRadius: 2,
              p: 2,
              mb: 1.5,
              opacity: 0.45,
              cursor: 'default',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{ fontWeight: 600, color: theme.text, fontSize: 14 }}
              >
                Freestyle Inspection
              </Typography>
              <Chip
                label="Mobile Only"
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  backgroundColor: `${theme.textLight}20`,
                  color: theme.textLight,
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}>
              Move at your own pace. Capture what you see and we will build your
              report.
            </Typography>
          </Box>

          {/* Upload & Generate - gated behind notesToHank flag (same as mobile) */}
          {featureFlag('notesToHank') && (
            <Box
              onClick={() => {
                setShowInspectionModes(false);
                setShowAIPromptChat(true);
              }}
              sx={{
                border: `2px solid ${theme.primary}`,
                borderRadius: 2,
                p: 2,
                mb: 1.5,
                cursor: 'pointer',
                backgroundColor: `${theme.primary}08`,
                '&:hover': { backgroundColor: `${theme.primary}15` },
              }}
            >
              <Typography
                sx={{ fontWeight: 600, color: theme.primary, fontSize: 14 }}
              >
                Upload & Generate
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}
              >
                Upload photos and chat with AI to build and edit your report.
              </Typography>
            </Box>
          )}

          {/* Dismiss */}
          <Box
            onClick={() => setShowInspectionModes(false)}
            sx={{
              border: `1px solid ${theme.stroke}`,
              borderRadius: 2,
              py: 1.5,
              mb: 2,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: theme.background2 },
            }}
          >
            <Typography sx={{ fontSize: 13, color: theme.textLight }}>
              Never Mind
            </Typography>
          </Box>
        </Box>
      </Dialog>

      {/* ── AI Prompt Chat Modal ─────────────────────────── */}
      <Dialog
        open={showAIPromptChat}
        onClose={() => {
          setShowAIPromptChat(false);
          refreshChecklist();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh',
            backgroundColor: theme.background1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.stroke}`,
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: theme.text }}>
            AI Inspection
          </Typography>
          <IconButton
            onClick={() => {
              setShowAIPromptChat(false);
              refreshChecklist();
            }}
            size="small"
            sx={{ color: theme.text }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {dealID && <NotesToHankChat dealId={dealID} dealInfo={dealInfo} />}
        </DialogContent>
      </Dialog>

      {/* ── Checklist View Modal ──────────────────────────── */}
      <Dialog
        open={showChecklistModal}
        onClose={() => setShowChecklistModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh',
            backgroundColor: theme.background1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.stroke}`,
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: theme.text }}>
            {activeChecklistName || 'Inspection Checklist'}
          </Typography>
          <IconButton
            onClick={() => setShowChecklistModal(false)}
            size="small"
            sx={{ color: theme.text }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {dealID && (
            <DealInspectionQA
              dealID={dealID}
              dealFiles={
                Array.isArray(dealInfo?.files?.items)
                  ? dealInfo.files.items
                  : []
              }
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ClaimTab;
